import {
  canDownloadFile,
  canOfferFileDuplication,
} from "@/lib/data/requests/files/file-adapter";
import { ContextMenu, ContextMenuItem } from "@/components/ui";
import { MatterFileMoveDialog } from "@/components/features/file-library/MatterFileMoveDialog";
import { MY_FILES_UI_ENABLED } from "@/components/features/file-library/visibility";
import { useState } from "react";
import { CopyPlus, Download, FolderInput } from "lucide-react";
import type { ReactNode } from "react";

type GlobalFileMenuItem = {
  isFolder?: boolean;
  name: string;
  presignedUrlKey?: string | null;
  revision?: number;
  capabilities?: { canDownload: boolean };
  systemFolder?: string | null;
  skillId?: string | null;
};

type GlobalFileContextMenuProps = {
  canDelete: boolean;
  canEdit: boolean;
  canDuplicate?: boolean;
  canDuplicateFile?: boolean;
  matterFileId?: string;
  item: GlobalFileMenuItem;
  onDelete: () => void;
  onDownload: () => void;
  onDuplicate?: () => void;
  onDuplicateFile?: () => void;
  onEdit: () => void;
  trigger: ReactNode;
};

export function GlobalFileContextMenu({
  canDelete,
  canEdit,
  canDuplicate = false,
  canDuplicateFile = false,
  matterFileId,
  item,
  onDelete,
  onDownload,
  onDuplicate,
  onDuplicateFile,
  onEdit,
  trigger,
}: GlobalFileContextMenuProps) {
  const [moving, setMoving] = useState(false);
  const entityName = item.isFolder ? "folder" : "file";
  const canDownload = canDownloadFile(item);

  return (
    <>
      {" "}
      <ContextMenu
        entityName={entityName}
        open={canEdit ? { onSelect: onEdit } : false}
        copy={{
          label: `Copy ${entityName} name`,
          successLabel: `${item.isFolder ? "Folder" : "File"} name`,
          value: item.name,
        }}
        delete={canDelete ? { onSelect: onDelete } : false}
        trigger={trigger}
        contentClassName="min-w-[200px]"
      >
        {matterFileId && !item.isFolder && canEdit && (
          <ContextMenuItem
            onSelect={() => {
              setMoving(true);
            }}
          >
            <FolderInput />
            {MY_FILES_UI_ENABLED
              ? "Move to my files or firm files"
              : "Move to firm files"}
          </ContextMenuItem>
        )}
        {canDownload ? (
          <ContextMenuItem onSelect={onDownload}>
            <Download />
            Download file
          </ContextMenuItem>
        ) : null}
        {canDuplicate ? (
          <ContextMenuItem onSelect={onDuplicate}>
            <CopyPlus />
            Duplicate template
          </ContextMenuItem>
        ) : null}
        {canDuplicateFile && canOfferFileDuplication(item) ? (
          <ContextMenuItem onSelect={onDuplicateFile}>
            <CopyPlus />
            Duplicate {entityName}
          </ContextMenuItem>
        ) : null}
      </ContextMenu>
      {moving && matterFileId && (
        <MatterFileMoveDialog
          id={matterFileId}
          name={item.name}
          onClose={() => {
            setMoving(false);
          }}
        />
      )}
    </>
  );
}
